import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { WaiterUser, ScannedClient, WaiterCartItem, WaiterOrder } from '../types/waiter';

const API_BASE = '/api';

interface WaiterStore {
  // Auth
  waiter: WaiterUser | null;
  isAuthenticated: boolean;
  login: (phone: string, pin: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;

  // Scanned client
  client: ScannedClient | null;
  setClient: (client: ScannedClient | null) => void;
  loadClientByQR: (data: { id: number; name: string; phone: string }) => Promise<void>;

  // Cart
  cart: WaiterCartItem[];
  tableNumber: number;
  addToCart: (item: WaiterCartItem) => void;
  removeFromCart: (id: string) => void;
  updateQuantity: (id: string, qty: number) => void;
  clearCart: () => void;
  setTableNumber: (n: number) => void;
  getCartTotal: () => number;

  // Orders
  orders: WaiterOrder[];
  addOrder: (order: WaiterOrder) => void;
  updateOrderStatus: (orderId: string, status: WaiterOrder['status']) => void;
  setOrderPayment: (orderId: string, method: 'cash' | 'card') => void;
}

export const useWaiterStore = create<WaiterStore>()(
  persist(
    (set, get) => ({
      waiter: null,
      isAuthenticated: false,

      async login(phone: string, pin: string) {
        try {
          const resp = await fetch(`${API_BASE}/waiters/auth`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ phone, pin }),
          });
          const data = await resp.json();
          if (data.success && data.waiter) {
            set({ waiter: data.waiter, isAuthenticated: true });
            return { success: true };
          }
          return { success: false, error: data.error || 'Неверный телефон или PIN' };
        } catch (e: any) {
          return { success: false, error: e.message || 'Ошибка подключения' };
        }
      },

      logout() {
        set({ waiter: null, isAuthenticated: false, client: null, cart: [], tableNumber: 1, orders: [] });
        localStorage.removeItem('waiter-storage');
      },

      client: null,
      setClient(client) { set({ client }); },

      async loadClientByQR(data) {
        try {
          let name = data.name;
          let phone = data.phone;

          // Если имя не указано (ввод по ID) — загрузить из Universe API
          if (!name || name === 'Гость' || !phone) {
            try {
              const clientResp = await fetch(`${API_BASE}/universe`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ method: 'get_client', ClientId: data.id }),
              });
              const clientData = await clientResp.json();
              if (clientData && clientData.errorCode === '0') {
                const fn = clientData.first_name || clientData.FirstName || '';
                const ln = clientData.last_name || clientData.LastName || '';
                name = `${ln} ${fn}`.trim() || name;
                phone = clientData.phone_number || clientData.Phone || phone;
              }
            } catch {
              // Оставляем как есть
            }
          }

          // Get effective bonus balance
          const resp = await fetch(`${API_BASE}/bonuses/${data.id}/effective-balance`);
          const bonus = await resp.json();
          const bal = bonus.balance || {};
          const client: ScannedClient = {
            id: data.id,
            name,
            phone,
            bonusBalance: bal.raw || 0,
            effectiveBalance: bal.effective || 0,
          };
          set({ client });
        } catch {
          const client: ScannedClient = {
            id: data.id,
            name: data.name,
            phone: data.phone,
            bonusBalance: 0,
            effectiveBalance: 0,
          };
          set({ client });
        }
      },

      cart: [],
      tableNumber: 1,

      addToCart(item) {
        const cart = get().cart;
        const existing = cart.find(i => i.id === item.id);
        if (existing) {
          set({ cart: cart.map(i => i.id === item.id ? { ...i, quantity: i.quantity + 1 } : i) });
        } else {
          set({ cart: [...cart, { ...item, quantity: 1 }] });
        }
      },

      removeFromCart(id) {
        set({ cart: get().cart.filter(i => i.id !== id) });
      },

      updateQuantity(id, qty) {
        if (qty <= 0) {
          set({ cart: get().cart.filter(i => i.id !== id) });
        } else {
          set({ cart: get().cart.map(i => i.id === id ? { ...i, quantity: qty } : i) });
        }
      },

      clearCart() { set({ cart: [] }); },
      setTableNumber(n) { set({ tableNumber: n }); },

      getCartTotal() {
        return get().cart.reduce((sum, i) => sum + (i.price - (i.discount || 0)) * i.quantity, 0);
      },

      orders: [],

      addOrder(order) {
        set({ orders: [order, ...get().orders] });
      },

      updateOrderStatus(orderId, status) {
        set({ orders: get().orders.map(o => o.id === orderId ? { ...o, status } : o) });
      },

      setOrderPayment(orderId, method) {
        set({ orders: get().orders.map(o => o.id === orderId ? { ...o, paymentMethod: method, status: 'paid' } : o) });
      },
    }),
    {
      name: 'waiter-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        waiter: state.waiter,
        isAuthenticated: state.isAuthenticated,
        orders: state.orders,
      }),
    }
  )
);
